import React, {useRef, useState, useEffect} from 'react';
import mapboxgl from "mapbox-gl";
import {useDispatch, useSelector} from "react-redux";
import {getLocations, installationSelect} from "../../../redux-store/selectors";
import redIcon from "../../../images/red-icon.png";
import greenIcon from "../../../images/green-icon.png";
import blueIcon from "../../../images/blue-icon.png";
import yellowIcon from "../../../images/yellow-icon.png";
import orangeIcon from "../../../images/orange-icon.png";
import MapSideDrawer from "./MapSideDrawer";
import * as turf from "@turf/turf";

import "../../../css/map.css"

mapboxgl.accessToken = `${process.env["REACT_APP_MAPBOX_TOKEN"]}`;
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

const levelPriorityMapping = {
    "RED": 5,
    "ORANGE": 4,
    "YELLOW": 3,
    "BLUE": 2,
}

const generateFeatureCollection = (data) => {

    return {
        "type": "FeatureCollection",
        "features": data.map(item => {

            const levelPriority = item.level === null ? 1 : levelPriorityMapping[item.level];
            return {
                "type": "Feature",
                "geometry": {
                    "type": "Point",
                    "coordinates": [item.longitude, item.latitude]
                },
                'properties': {
                    "id": item.id,
                    "name": item.name,
                    "lat": item.latitude,
                    "long": item.longitude,
                    "description": item.description,
                    "level": item.level,
                    "icon": item.level === null ? "GREEN" : item.level,
                    "priority": levelPriority,
                },
            }
        })
    }
}
const MapLocation = () => {

    const dispatch = useDispatch();
    const mapContainer = useRef(null);
    const map = useRef(null);
    const installation = useSelector(installationSelect);

    const [lng, setLng] = useState(16.4);
    const [lat, setLat] = useState(48.2);
    const [zoom, setZoom] = useState(3);

    const [showDrawer, setShowDrawer] = useState(false);
    const [location, setLocation] = useState(null);

    const [mapLoaded, setMapLoaded] = useState(false);

    const locations = useSelector(getLocations);

    useEffect(() => {
        if (map.current || !mapContainer.current) return;
        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [lng, lat],
            zoom: zoom,
            attributionControl: false,
            minZoom: 4,
            maxZoom: 18
        })
        map.current.addControl(new mapboxgl.ScaleControl({}), "bottom-right")
        map.current.on("load", () => {
            map.current.loadImage(redIcon, (error, image) => {
                map.current.addImage('RED', image, {'sdf': false});
            });
            map.current.loadImage(orangeIcon, (error, image) => {
                map.current.addImage('ORANGE', image, {'sdf': false});
            });
            map.current.loadImage(blueIcon, (error, image) => {
                map.current.addImage('BLUE', image, {'sdf': false});
            });
            map.current.loadImage(greenIcon, (error, image) => {
                map.current.addImage('GREEN', image, {'sdf': false});
            });
            map.current.loadImage(yellowIcon, (error, image) => {
                map.current.addImage('YELLOW', image, {'sdf': false});
            });
            setMapLoaded(true);
        })
    }, []);

    useEffect(() => {
        if (map.current === null || locations.length === 0 || !mapLoaded) return;
        try {
            const source = map.current.getSource("locations");
            let featureCollection = generateFeatureCollection(locations);
            if (locations.length > 0) {
            
                const bbox = turf.bbox({
                    type: "FeatureCollection",
                    features: featureCollection.features,
                })
                map.current.fitBounds(bbox, {animate: false, padding: 20});

            }
            if (source !== undefined) {
                source.setData(featureCollection);
                return;
            }
            const geoJson = {
                "type": "geojson",
                "data": featureCollection,
            }
            map.current.addSource("locations", geoJson);
            map.current.on("click", "locations", e => {
                const id = e.features[0].properties.id;
                const name = e.features[0].properties.name;
                const latitude = e.features[0].properties.lat;
                const longitude = e.features[0].properties.long;
                const description = e.features[0].properties.description;
                const level = e.features[0].properties.level;
                setLocation({id, name, latitude, longitude, description, level: level === undefined ? null : level})
                setShowDrawer(true);
                map.current.setFeatureState({
                    source: "locations",
                    sourceLayer: "locations",
                    id: id,
                }, {
                    hover: true
                })
            })
            map.current.addLayer({
                "id": "locations",
                "type": "symbol",
                "source": "locations",
                'layout': {
                    'icon-image': ['image', ['get', 'icon']],
                    'text-field': ['get', 'name'],
                    'text-size': 10,
                    'text-anchor': "top",
                    'text-offset': [0, 0.5],
                    'text-optional': true,
                    'text-allow-overlap': false,
                    'icon-size': 0.4,
                    'icon-allow-overlap': true,
                    'symbol-z-order': "source",
                    'symbol-sort-key': ['get', 'priority']
                },
            })
            map.current.on('mouseenter', 'locations', () => {
                map.current.getCanvas().style.cursor = 'pointer';
            });
            map.current.on('mouseleave', 'locations', () => {
                map.current.getCanvas().style.cursor = '';
            });
        } catch (err) {
            console.log(err);
        }
    }, [locations, mapLoaded]);

    const onDrawerClose = () => {
        setShowDrawer(false);
    }
    return (
        <>
            <div style={{width: "100%", height: "calc(100vh - 182.5px)", position: "relative", background: "#efe9e1"}}>

                <div ref={mapContainer} className={"map-container"}>
                    <MapSideDrawer show={showDrawer} onClose={onDrawerClose} location={location}
                                   installationId={installation?.value}/>
                </div>
            </div>
        </>
    );
};
export default MapLocation;
